import { useState, type ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import { useReadinessStore } from '@/stores/readinessStore'
import { Chip, Icon } from '@/components/readiness/primitives'
import { HiddenForIndex } from '@/components/migration/primitives'
import { ReviewExportModal } from '@/components/migration/ReviewExportModal'
import { MarkSubmittedModal, SetDateModal } from '@/components/migration/SubmissionModals'
import { incompleteSections, lastEdit, submissionChecklist } from '@/lib/migration/submission'
import { timeAgo } from '@/lib/readiness/util'
import { formatDate } from '@/components/common/format'
import { LockIcon } from '@/components/readiness/LockedItem'
import { useIsLocked } from '@/stores/profileStore'

// ── Page frames (Iteration 3 §1.2, §6, §8) ──────────────────────────────────
// The feature tab lives in the module strip; these frames carry the page
// title, the page's own actions and the page-level tabs (44px underline, so
// the two tiers read as distinct).

function PageTabs({ tabs, label }: { tabs: Array<{ to: string; label: string; end?: boolean; chip?: ReactNode; testId: string }>; label: string }) {
  return (
    <div className="mb-4 flex flex-wrap gap-1 border-b" style={{ borderColor: 'var(--rd-border-subtle)' }} role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} role="tab" className="-mb-px flex h-11 items-center gap-1.5 px-[14px] text-[14px] font-semibold" style={({ isActive }) => ({ color: isActive ? 'var(--rd-text-primary)' : 'var(--rd-text-muted)', borderBottom: `2px solid ${isActive ? 'var(--rd-accent-primary)' : 'transparent'}` })} data-testid={t.testId}>
          {t.label}
          {t.chip}
        </NavLink>
      ))}
    </div>
  )
}

function Frame({ title, subtitle, right, back, children, testId }: { title: string; subtitle: ReactNode; right?: ReactNode; back?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <div className="rd -m-6 min-h-[calc(100vh-109px)] p-4 sm:p-6" style={{ background: 'var(--rd-bg-page)' }} data-testid={testId}>
      {back && <div className="-mt-2 mb-3" data-testid="back-link">{back}</div>}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="rd-page-title">{title}</h1>
          <p className="mt-1 text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>{subtitle}</p>
        </div>
        {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
      </div>
      {children}
    </div>
  )
}

// ── CSA Submission (§6): Overview + Submission plan; draft / submitted ──────

export function SubmissionShell({ ctx, children, testId, right, back }: { ctx: MigrationCtx; children: ReactNode; testId?: string; right?: ReactNode; back?: ReactNode }) {
  const { index, indexId, orgId, summary, enabled, plan: data, actions: planActions, mscope } = ctx
  const navigate = useNavigate()
  const track = useReadinessStore((s) => s.track)
  const [modal, setModal] = useState<null | 'date' | 'review' | 'mark'>(null)
  const [toast, setToast] = useState<string | null>(null)
  const q = `?index=${indexId}`
  const st = data.plan?.plan_state
  const submitted = st?.status === 'submitted'
  const current = submitted ? data.submissions.filter((x) => x.version === st!.n).pop() ?? null : null
  const previous = data.submissions.filter((x) => !st || x.version < st.n).pop() ?? null
  const edit = data.plan ? lastEdit(data) : null
  const missing = summary?.missing_systems.length ?? 0
  const due = st?.submission_date ?? summary?.due?.date ?? null
  const daysLeft = due ? Math.ceil((new Date(due).getTime() - Date.now()) / 86_400_000) : null

  const actions = enabled && data.plan && (
    submitted ? (
      <>
        {right}
        {current?.final_pdf_version_id && <Link to={`/readiness/submission/versions/${current.final_pdf_version_id}/print${q}`} className="rd-btn" data-testid="download-submitted-pdf"><Icon name="download" size={14} /> Download submitted PDF</Link>}
        <button type="button" className="rd-btn rd-btn-primary" onClick={() => { void planActions.startNewVersion().then((next) => setToast(`v${next.n} started as a draft from the submitted v${next.n - 1}`)).catch((e: Error) => setToast(e.message)) }} data-testid="start-new-version"><Icon name="plus" size={14} /> Start new version · v{st!.n + 1}</button>
      </>
    ) : (
      <>
        {right}
        <button type="button" className="rd-btn" onClick={() => setModal('date')} data-testid="set-submission-date"><Icon name="calendar" size={14} /> Set submission date</button>
        <button type="button" className="rd-btn" onClick={() => setModal('review')} data-testid="review-export-plan"><Icon name="download" size={14} /> Review & export plan</button>
        <button type="button" className="rd-btn rd-btn-primary" onClick={() => setModal('mark')} data-testid="mark-submitted"><Icon name="check" size={14} /> Mark as submitted</button>
      </>
    )
  )
  // Page-tab chip (I4 §9g): `DRAFT` or `LOCKED`; the strip chip carries the version.
  const stateChip = (locked: boolean) => (st ? <Chip tone={submitted ? 'ok' : 'info'} className="rd-chip-sm" style={{ letterSpacing: '0.06em' }}>{submitted ? (locked ? 'LOCKED' : 'SUBMITTED') : 'DRAFT'}</Chip> : null)

  return (
    <Frame title="CSA Submission Plan" subtitle="One CSA submission plan per organisation · one system annex per CII system · prepared, submitted, locked and revisited in versions" right={actions} back={back} testId={testId}>
      {enabled && data.plan && st && (
        <>
          {/* Status banner (§6.2) on every tab */}
          {submitted ? (
            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[10px] px-4 py-3 text-[12.5px]" style={{ background: 'var(--rd-status-ok-bg)', color: 'var(--rd-text-secondary)' }} role="status" data-testid="submission-banner">
              <span className="flex items-center gap-1.5 font-semibold" style={{ color: 'var(--rd-status-ok-text)' }}><LockIcon /> SUBMITTED · v{st.n}</span>
              {current && <span>Submitted {formatDate(current.submitted_at)} · {new Date(current.submitted_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} {current.time_zone} by {current.user_id}{current.csa_reference ? ` · CSA reference ${current.csa_reference}` : ''}{current.acknowledgement_id ? ' · acknowledgement attached' : ''}</span>}
              <span className="ml-auto" style={{ color: 'var(--rd-text-muted)' }}>Read-only. Changes go into a new version.</span>
            </div>
          ) : (
            <div className="mb-4 rounded-[10px] px-4 py-3 text-[12.5px]" style={{ background: 'var(--rd-bg-card)', border: '1px solid var(--rd-border-subtle)' }} role="status" data-testid="submission-banner">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Chip tone="info" style={{ fontWeight: 700, letterSpacing: '0.06em' }}>DRAFT v{st.n}</Chip>
                <span style={{ color: 'var(--rd-text-secondary)' }}>{due ? `Submission due ${formatDate(due)} · ${daysLeft} d left` : 'No submission date'}{edit ? ` · last edited ${timeAgo(edit.at)} by ${edit.by}` : ''}</span>
                {previous && (
                  <span style={{ color: 'var(--rd-text-muted)' }}>
                    Previous: v{previous.version} submitted {formatDate(previous.submitted_at)}{previous.final_pdf_version_id && <> · <Link to={`/readiness/submission/versions/${previous.final_pdf_version_id}/print${q}`} className="rd-link">view</Link></>}
                  </span>
                )}
                {/* Blocking line: right-aligned on the status row at ≥ 1280, wraps below (I4 §9h). */}
                {missing > 0 && <span className="w-full font-semibold min-[1280px]:ml-auto min-[1280px]:w-auto" style={{ color: 'var(--rd-status-warn)' }} data-testid="banner-blocking">{missing} CII {missing === 1 ? 'system' : 'systems'} without an annex block submission</span>}
              </div>
            </div>
          )}
          <PageTabs
            label="CSA Submission sections"
            tabs={[
              { to: `/readiness/submission${q}`, label: 'Overview', end: true, testId: 'tab-overview' },
              { to: `/readiness/submission/plan${q}`, label: 'Submission plan', chip: stateChip(true), testId: 'tab-submission-plan' },
            ]}
          />
          <p className="-mt-2 mb-4 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Migration items and the roadmap moved to the <Link to={`/readiness/tracker${q}`} className="rd-link">Migration Tracker →</Link></p>
          {toast && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] px-4 py-2.5 text-[12.5px]" style={{ background: 'var(--rd-status-ok-bg)', color: 'var(--rd-status-ok-text)' }} role="status" data-testid="export-toast">
              <Icon name="check" size={14} /> {toast} <button type="button" className="rd-link ml-auto" onClick={() => setToast(null)}>Dismiss</button>
            </div>
          )}
        </>
      )}
      {enabled ? children : <HiddenForIndex indexId={indexId} name={index.name} />}
      {modal === 'date' && <SetDateModal ctx={ctx} onClose={() => setModal(null)} />}
      {modal === 'review' && <ReviewExportModal ctx={ctx} onClose={() => setModal(null)} onDone={(v, files) => { setModal(null); setToast(files.length ? `${v.label} exported · ${files.join(', ')}` : `${v.label} saved`) }} />}
      {modal === 'mark' && data.plan && summary && (
        <MarkSubmittedModal
          ctx={ctx}
          openChecks={submissionChecklist(summary, data, mscope).filter((c) => !c.ok).map((c) => c.label)}
          incomplete={incompleteSections(summary)}
          onClose={() => setModal(null)}
          onDone={(rec) => {
            setModal(null)
            track(orgId, indexId, { name: 'readiness.submission_marked', version: rec.version })
            setToast(`v${rec.version} marked as submitted and locked`)
            navigate(`/readiness/submission${q}`)
          }}
        />
      )}
    </Frame>
  )
}

// The lock glyph moved to the shared LockedItem (I6 POC §2); kept here for existing imports.
export { LockIcon }

// ── Migration Tracker (§8): Migration items + Roadmap ───────────────────────

export function TrackerShell({ ctx, children, testId, onNewItem, onFromFindings }: { ctx: MigrationCtx; children: ReactNode; testId?: string; onNewItem?: () => void; onFromFindings?: ReactNode }) {
  const { index, indexId, enabled, summary } = ctx
  const q = `?index=${indexId}`
  // POC (I6 POC §5.2): the page's own actions read disabled on the locked preview.
  const locked = useIsLocked('migration_tracker')
  return (
    <Frame
      title="Migration Tracker"
      subtitle="Migration work across the estate · system › component › migration item › assets"
      testId={testId}
      right={enabled && (
        <>
          {onFromFindings}
          {onNewItem && <button type="button" className="rd-btn rd-btn-primary" onClick={onNewItem} disabled={locked} style={locked ? { opacity: 0.4 } : undefined} data-testid="new-migration-item"><Icon name="plus" size={14} /> Migration item</button>}
        </>
      )}
    >
      {enabled && (
        <PageTabs
          label="Migration Tracker sections"
          tabs={[
            { to: `/readiness/tracker${q}`, label: 'Migration items', end: true, chip: summary ? <span className="rd-mono ml-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{summary.items.total}</span> : null, testId: 'tab-migration-items' },
            { to: `/readiness/tracker/roadmap${q}`, label: 'Roadmap', testId: 'tab-roadmap' },
          ]}
        />
      )}
      {enabled ? children : <HiddenForIndex indexId={indexId} name={index.name} />}
    </Frame>
  )
}
